import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import { ROUTES } from "@/routes";
import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { opcenter } from "../../../config.json";
import { useTranslation } from "react-i18next";
import { enableMockServer, getEnvSettings } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton/skeleton";
import { ExternalLinkIcon } from "lucide-react";
import { useFullScreen } from "@/lib/use-full-screen";

export function OpCenterDialog() {
  const navigate = useNavigate();
  const { exitFullscreen } = useFullScreen();

  const handleOpenChange = useCallback(
    (open: boolean) => !open && setTimeout(() => navigate(ROUTES.HOME), 500),
    [navigate]
  );

  const handleExternalClick = useCallback(() => {
    handleOpenChange(false);
    exitFullscreen();
  }, [handleOpenChange, exitFullscreen]);

  return (
    <Dialog defaultOpen onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[95%] h-[95%]">
        <OpCenterDialogContent onExternalClick={handleExternalClick} />
      </DialogContent>
    </Dialog>
  );
}

function OpCenterDialogContent({
  onExternalClick,
}: {
  onExternalClick?: VoidFunction;
}) {
  const { resource } = useParams();
  const [params] = useSearchParams();
  const title = params.get("title");
  const url = getUrl(resource) && `${getUrl(resource)}?${params}`;

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <div className="flex w-full items-center gap-x-4">
            <div>{title}</div>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              onClick={onExternalClick}
            >
              <ExternalLinkIcon className="h-4 w-4" />
            </a>
          </div>
        </DialogTitle>
      </DialogHeader>
      <DialogBody className="bg-header-foreground grid h-full p-0">
        <div className="h-full w-full overflow-hidden relative">
          <OpCenterDialogBody url={url} />
        </div>
      </DialogBody>
    </>
  );
}

function OpCenterDialogBody(props: { url?: string }) {
  const { url } = props;
  const { t } = useTranslation();
  const { resource } = useParams();
  const [loading, setLoading] = useState(true);

  if (!url) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <span className="text-destructive font-bold text-2xl uppercase">
          {t("opcenter.invalidResource")}
        </span>
        <span>{resource}</span>
      </div>
    );
  }

  if (enableMockServer()) {
    return (
      <div className="text-input h-full flex items-center justify-center font-bold text-2xl uppercase">
        Opcenter pages are not available in mocked environment
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div className="flex gap-x-4 w-full h-full absolute z-10 inset-0 bg-white p-5">
          <Skeleton className="h-full w-20" />
          <div className="flex flex-col gap-y-4 h-full w-full">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-full w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <Skeleton className="h-full w-20" />
        </div>
      )}
      <iframe
        src={props.url}
        className="h-[calc(100%+56px)] w-[calc(100%+73px)] top-[-56px] left-[-65px] shadow-none absolute z-0"
        title="OpCenter Viewer"
        allowFullScreen
        onLoad={() => setLoading(false)}
      />
    </>
  );
}

function getUrl(resource?: string) {
  if (!resource) return;
  const { baseUrl, sitHost } = getEnvSettings();
  return URL.parse(
    `${opcenter.base.replace("<SITHOST>", sitHost)}/${resource}`,
    baseUrl
  )?.toString();
}
